import React from 'react';
import {
    LocalizationProvider as MuiDatePickersLocalizationProvider,
    daDK as datePickersDaDk,
    deDE as datePickersDeDE,
    esES as datePickersEsES,
    enUS as datePickersEnUS,
    nbNO as datePickersNbNO,
} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';
import {ModernFamlyContext, type SupportedLanguage} from '@famly/mf_system_provider';

export const DatePickersLocalizationProvider = (props: {children: React.ReactNode}) => {
    const {locale} = React.useContext(ModernFamlyContext);

    const momentLocale = languageToMomentLocale(locale);
    const localeText = languageToLocalization(locale);

    return (
        <MuiDatePickersLocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={momentLocale}
            localeText={localeText.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            {props.children}
        </MuiDatePickersLocalizationProvider>
    );
};

/**
 * Maps a known language to a known moment locale. This has to be kept in
 * sync with app/setup/moment-setup.js
 */
const languageToMomentLocale = (supportedLanguage: SupportedLanguage) => {
    switch (supportedLanguage) {
        case 'enGB':
            return 'en_GB';
        case 'daDK':
            return 'da_DK';
        case 'deCH':
        case 'deDE':
            return 'de';
        case 'enUS':
            return 'en_US';
        case 'esES':
            return 'es';
        case 'esUS':
            return 'es-us';
        case 'nbNO':
            return 'nb';
        default:
            return 'en_US';
    }
};

type DatePickersLocalization = typeof datePickersDaDk;
const languageToLocalization = (supportedLanguage: SupportedLanguage): DatePickersLocalization => {
    switch (supportedLanguage) {
        case 'daDK': {
            return datePickersDaDk;
        }
        case 'deCH':
        case 'deDE': {
            return datePickersDeDE;
        }
        case 'esUS':
        case 'esES': {
            return datePickersEsES;
        }
        case 'enGB':
        case 'enUS': {
            return datePickersEnUS;
        }
        case 'nbNO': {
            return datePickersNbNO;
        }
        default:
            exhaustiveCheck(supportedLanguage);
            return datePickersEnUS;
    }
};
